import * as React from "react";
import { cn } from "lib/utils";

export interface CardIconProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  size?: "sm" | "md" | "lg";
}

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "glass" | "white" | "whiteGlass";
  maxWidth?: "default" | "narrow" | "large" | "none";
  padding?: "none" | "compact" | "default" | "large";
  disableAnimation?: boolean;
  disableGlass?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = "default",
      maxWidth = "default",
      padding = "default",
      disableAnimation,
      disableGlass,
      ...props
    },
    ref
  ) => {
    const variantClasses = {
      default: "bg-white/30 border-0", // Changed default to glass, leaving default for backwards compatibility
      glass: "bg-white/30 border-0",
      whiteGlass: "bg-white/60 border-0",
      white: "bg-white border-0",
    };

    const maxWidthClasses = {
      default: "max-w-[23.75rem] mx-4",
      narrow: "max-w-[21.25rem] mx-4",
      large: "max-w-[40.625rem]",
      none: "max-w-none",
    };

    const paddingClasses = {
      none: "",
      compact: "p-2",
      default: "py-4 px-6",
      large: "p-10",
    };

    const glassClasses = disableGlass
      ? ""
      : "";

    return (
      <div
        ref={ref}
        className={cn(
          "w-full rounded-3xl z-10",
          glassClasses,
          !disableAnimation && "animate-fade-in opacity-0",
          variantClasses[variant],
          maxWidthClasses[maxWidth],
          paddingClasses[padding],
          className
        )}
        {...props}
      />
    );
  }
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 text-foundation-base-darkbase-dark-300 font-semibold text-[length:var(--body-s-font-size)]", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

export interface CardTitleProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  centered?: boolean;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  size?: "default" | "small" | "big";
  weight?: "default" | "normal" | "medium" | "semibold" | "bold";
  subtitleSize?: "default" | "small" | "extraSmall" | 'big';
}

const CardTitle = React.forwardRef<
  HTMLDivElement,
  CardTitleProps
>(
  (
    {
      className,
      centered = false,
      title,
      subtitle,
      subtitleSize = 'default',
      children,
      size = "default",
      weight = "default",
      ...props
    },
    ref
  ) => {
    const titleClasses = {
      default:
        "text-heading font-headings-h3 font-[var(--headings-h1-font-weight)] text-[length:var(--headings-h3-font-size)] tracking-[var(--headings-h1-letter-spacing)] leading-[var(--headings-h2-line-height)] [font-style:var(--headings-h1-font-style)]",
      small:
        "text-heading font-headings-h3 font-[var(--headings-h3-font-weight)] text-[length:var(--headings-h4-font-size)] tracking-[var(--headings-h3-letter-spacing)] leading-[var(--headings-h5-line-height)] [font-style:var(--headings-h3-font-style)]",
      big:
        "text-heading font-headings-h3 font-[var(--headings-h1-font-weight)] text-[length:var(--headings-h2-font-size)] tracking-[var(--headings-h1-letter-spacing)] leading-[var(--headings-h2-line-height)] [font-style:var(--headings-h1-font-style)]",
    };

    const weightClasses = {
      default: "",
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    } as const;

    const subtitleClasses = {
      default:
        "text-muted-foreground font-headings-h3 font-[var(--headings-h3-font-weight)] text-[length:var(--body-s-font-size)] tracking-[var(--headings-h3-letter-spacing)] leading-[var(--body-s-line-height)] [font-style:var(--headings-h3-font-style)] mt-1 mb-4",
      small:
        "text-muted-foreground font-body-s font-[var(--body-s-font-weight)] text-[length:var(--body-s-font-size)] tracking-[var(--body-s-letter-spacing)] leading-[var(--body-s-line-height)] [font-style:var(--body-s-font-style)] mb-2",
      extraSmall:
        "text-muted-foreground font-body-s font-[var(--body-s-font-weight)] text-[0.7rem] tracking-[var(--body-s-letter-spacing)] leading-[var(--body-s-line-height)] [font-style:var(--body-s-font-style)] mb-2",
        big:
        "text-muted-foreground font-headings-h3 font-[var(--headings-h3-font-weight)] text-[length:var(--body-m-font-size)] tracking-[var(--headings-h3-letter-spacing)] leading-[var(--body-s-line-height)] [font-style:var(--headings-h3-font-style)] mt-1",
    };

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col pb-1",
          centered ? "items-center text-center" : "items-start text-left",
          className
        )}
        {...props}
      >
        {title && (
          <h1
            className={cn(
              titleClasses[size],
              weightClasses[weight],
              typeof title === "string" && "whitespace-pre-line"
            )}
          >
            {typeof title === "string" ? title.replace(/\\n/g, "\n") : title}
          </h1>
        )}
        {subtitle && (
          <div className={subtitleClasses[subtitleSize]}>
            {subtitle}
          </div>
        )}
        {children}
      </div>
    );
  }
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "flex-row" | "custom" | "form" | "userResponse";
  padding?: "default" | "large" | "compact" | "none";
}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, variant = "default", padding = "default", ...props }, ref) => {
    const variantClasses = {
      default: "flex flex-col items-center gap-4",
      "flex-row": "flex items-start gap-8",
      custom: "",
      form: "flex flex-col items-stretch gap-3",
      userResponse: "flex flex-col items-center justify-center gap-4 relative self-stretch w-full flex-[0_0_auto] bg-white/45"
    } as const;

    const paddingClasses = {
      default: "pt-2",
      large: "p-8",
      compact: "px-4 pb-6 pt-2",
      none: "p-0",
    } as const;

    return (
      <div
        ref={ref}
        className={cn(variantClasses[variant], paddingClasses[padding], className)}
        {...props}
      />
    );
  }
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardDescription,
  CardContent,
  CardTitle,
};

